import { Loader } from 'lucide-react';
import { type CSSProperties, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentLogoMarquee } from '@/components/agent-logo-marquee';

/**
 * The frame's 480px window on the strip, against the 584 the loading frame it
 * was drawn for uses. The fade stays at the stylesheet's 140px, which is what
 * both of those frames paint.
 */
const MARQUEE_STYLE = { '--agent-logo-marquee-max-width': '480px' } as CSSProperties;

/**
 * The wait between **Create Project** and the project's own page. Figma
 * 11914:190246.
 *
 * Not `components/agents-loading.tsx`, which is the same three parts in a
 * different composition: that frame is 584 wide with an 18px label on
 * text-secondary and no second line, this one is 480 with the label on
 * text-primary and a sentence under it saying where the user is about to land.
 * Sharing them would need props for width, colour and a subtitle to express a
 * difference the two frames genuinely have — the call agents-loading.tsx itself
 * records for the same near-duplication against project-list-loading.tsx. The
 * strip, which is the expensive part, is shared.
 *
 * `role="status"` on the copy and not the surface: the strip is `aria-hidden`
 * decoration, and a live region wrapping it would still have to be walked.
 *
 * `flex-1` and no background of its own: this renders inside the page's
 * `<main>`, which owns the region's fill and its scrolling. Growing to fill it
 * is what centres this block in the viewport.
 */
export const ProjectSetupLoading: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-14 lg:px-28">
      <div className="flex flex-col items-center gap-6">
        <AgentLogoMarquee style={MARQUEE_STYLE} />

        <div className="flex flex-col items-center gap-1" role="status">
          <div className="flex items-center gap-2">
            {/* Not motion-safe:animate-spin. Under `reduce` the strip stops, so
                this is the only motion left, and a frozen loading mark reads as
                a broken page — the call agents-loading.tsx makes and
                project-list-loading.tsx asserts. */}
            <Loader
              aria-hidden
              className="animate-spin text-[var(--ds-color-text-primary)]"
              size={24}
              strokeWidth={1.75}
            />
            {/* The frame's `heading 5` is 18/22 at weight 500 and the shipped
                --ds-typography-heading-5-* tokens are 16/20 at 400, so the size
                is written out rather than taken from a token that would quietly
                make it wrong. The same drift agents-loading.tsx records. */}
            <span className="text-lg font-medium leading-[22px] text-[var(--ds-color-text-primary)]">
              {t('project-creation.setting-up-title')}
            </span>
          </div>

          <p className="text-center text-[13px] leading-5 text-[var(--ds-color-raw-stone-600)]">
            {t('project-creation.setting-up-description')}
          </p>
        </div>
      </div>
    </div>
  );
};
